import { t } from '@lingui/macro';
import { BlogHero } from '@we-make-websites/ui-lib';
import type { BlogHeroProps } from '@we-make-websites/ui-lib';
import { NextSeo } from 'next-seo';
import { useContext } from 'use-context-selector';
import { BlogArticleContext } from 'components/blogArticleProvider/BlogArticleProvider';
import { BlogLandingContext } from 'components/blogLandingProvider/BlogLandingProvider';
import { BlogCategoryContext } from 'components/blogsCategoryProvider/BlogCategoryProvider';
import { Breadcrumbs } from 'components/breadcrumbs/Breadcrumbs';
import type { StoreSeo } from 'utils/types/storeTypes';

export const BlogLandingHeader = () => {
  const blogLandingContext = useContext(BlogLandingContext);

  if (blogLandingContext === undefined) {
    throw new Error(
      'BlogLandingHeader must be used within a BlogLandingContext'
    );
  }

  const { hero, seo } = blogLandingContext;

  return (
    <>
      <Breadcrumbs
        title={t({
          id: 'breadcrumbs.blogs',
          message: 'Blogs',
        })}
      />
      <BlogHeaderContent hero={hero} seo={seo} />
    </>
  );
};

export const BlogCategoryHeader = () => {
  const blogCategoryContext = useContext(BlogCategoryContext);

  if (blogCategoryContext === undefined) {
    throw new Error(
      'BlogCategoryHeader must be used within a BlogCategoryContext'
    );
  }

  const { category, hero, seo } = blogCategoryContext;

  return (
    <>
      <Breadcrumbs
        title={category}
        ancestors={[
          {
            title: t({
              id: 'breadcrumbs.blog',
              message: 'Blog',
            }),
            url: '/blog',
          },
        ]}
      />
      <BlogHeaderContent hero={hero} seo={seo} />
    </>
  );
};

export const BlogArticleHeader = () => {
  const blogArticleContext = useContext(BlogArticleContext);

  if (blogArticleContext === undefined) {
    throw new Error(
      'BlogArticleHeader must be used within a BlogArticleContext'
    );
  }

  const { seo, title, categoryRef } = blogArticleContext;

  const breadcrumbsAncestors = [
    {
      title: t({
        id: 'breadcrumbs.blog',
        message: 'Blog',
      }),
      url: '/blog',
    },
  ];

  if (categoryRef?.category && categoryRef.handle) {
    breadcrumbsAncestors.push({
      title: categoryRef?.category ?? '',
      url: `/blog/${categoryRef?.handle}`,
    });
  }

  return (
    <>
      <Breadcrumbs title={title} ancestors={breadcrumbsAncestors} />
      <BlogHeaderContent seo={seo} />
    </>
  );
};

const BlogHeaderContent = ({
  seo,
  hero,
}: {
  seo?: StoreSeo;
  hero?: BlogHeroProps;
}) => {
  return (
    <>
      <NextSeo title={seo?.title} description={seo?.description ?? ''} />
      {hero && <BlogHero {...hero} />}
    </>
  );
};
